<template>
  <div class="gs-app gm-integration-settings">
    <div class="w-full">
      <!-- Loader -->
      <portal to="loaderTarget">
        <div class="absolute bg-grey-100 bg-opacity-50 h-full left-0 top-0 w-full z-99" v-if="isLoading">
          <gd-loader :label="$t('frontend_loading')"/>
        </div>
      </portal>
      
      <!-- go back button -->
      <portal to="headerBreadCrumb">
        <div class="cursor-pointer self-center text-grey-500 hover:text-primary" @click="moveBack">
          <ExitIcon class="h-5 w-5"/>
        </div>
      </portal>

      <div id="user-view" class="gm-integration-settings__card flex flex-col bg-white rounded w-full pt-4">
        <div
          class="gm-integration-settings__header px-6 pt-1 pb-4 create-sender-header border-b border-grey-300"
        >
          <div class="text-xl text-lg font-bold">
            {{ $t('frontend_groovemail_settings_integeration_settings') }}
          </div>
          <div class="text-grey-800 text-sm">
            {{ $t('frontend_groovemail_settings_configure_integeration_settings') }}
          </div>
        </div>
        <div class="gm-integration-settings__body gs-refresh-controls relative p-6 pb-0">
          <div class="flex flex-col">
            <div class="flex flex-col mb-6">
              <label class="font-semibold text-sm text-grey-700 mb-2">
                {{ $t('frontend_groovemail_settings_integeration_settings_gateway_name') }}
              </label>
              <text-input
                variant="base"
                :validator="$v.model.gateway_name"
                v-model="model.gateway_name"
                :placeholder="$t('frontend_groovemail_settings_integeration_settings_enter_gateway_name')"
              />
            </div>

            <div class="flex flex-col mb-6">
              <label class="font-semibold text-sm text-grey-700 mb-2">{{
                $t('frontend_groovemail_settings_integeration_settings_account_sid')
              }}</label>
              <text-input
                variant="base"
                :validator="$v.model.account_sid"
                v-model="model.account_sid"
                :placeholder="$t('frontend_groovemail_settings_integeration_settings_enter_account_sid')"
              />
            </div>

            <div class="flex flex-col mb-6">
              <label class="font-semibold text-sm text-grey-700 mb-2">
                {{ $t('frontend_groovemail_settings_integeration_settings_auth_token') }}
              </label>
              <text-input
                variant="base"
                :validator="$v.model.auth_token"
                v-model="model.auth_token"
                :placeholder="$t('frontend_groovemail_settings_integeration_settings_enter_auth_token')"
              />
            </div>

            <div class="flex flex-col mb-6">
              <label class="font-semibold text-sm text-grey-700 mb-2">
                {{ $t('frontend_groovemail_settings_integeration_settings_phone_number') }}
              </label>
              <VuePhoneNumberInput
                v-if="this.pageType == 'edit'"
                class="sms-sender-phone-input"
                :border-radius="6"
                :default-country-code="default_country_code"
                :show-code-on-list="true"
                v-model="model.phone_number"
                @update="getPhoneData($event)"
              />
              <VuePhoneNumberInput
                v-if="this.pageType == 'add'"
                class="sms-sender-phone-input"
                :border-radius="6"
                :fetch-country="true"
                :show-code-on-list="true"
                v-model="model.phone_number"
                @update="getPhoneData($event)"
              />
              <p
                class="w-full error text-danger mt-1 text-sm"
                v-if="submitted && !this.phoneData.isValid"
              >
                {{ $t('frontend_groovemail_settings_integeration_invalid_phone_number') }}
              </p>
            </div>

            <div class="flex flex-col mb-6">
              <label class="font-semibold text-sm text-grey-700 mb-2">
                {{ $t('frontend_groovemail_settings_integeration_settings_test_phone_number_label') }}
              </label>
              <VuePhoneNumberInput
                class="sms-sender-phone-input"
                :border-radius="6"
                :fetch-country="true"
                :show-code-on-list="true"
                v-model="model.test_phone_number"
                @update="getTestPhoneData($event)"
              />
              <p
                class="w-full error text-danger mt-1 text-sm"
                v-if="submitted && !this.testPhoneData.isValid"
              >
                {{ $t('frontend_groovemail_settings_integeration_invalid_phone_number') }}
              </p>
            </div>

            <div class="flex flex-col mb-6">
              <label class="font-semibold text-sm text-grey-700 mb-2">
                {{ $t('frontend_groovemail_settings_integeration_settings_default') }}
              </label>
              <gd-toggle
                bgClass="bg-grey-300"
                :value="model.is_default"
                @change="changedefault"
              />
            </div>
          </div>
          <div class="flex justify-end pb-6 create-sender-save-btn">
            <gs-button
              v-if="this.pageType == 'add'"
              variant="primary"
              size="md"
              :disabled="this.userCanMail ? false : true"
              @click="
                userCanMail ? createIntegerations() : null
              "
              >{{ $t('frontend_save') }}</gs-button
            >
            <gs-button
              v-if="this.pageType == 'edit'"
              variant="primary"
              size="md"
              :disabled="this.userCanMail ? false : true"
              @click="userCanMail ? createIntegerations() : null"
              >{{ $t('frontend_update') }}</gs-button
            >
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import {
  required,
  minLength,
  maxLength,
} from 'vuelidate/lib/validators';

import VuePhoneNumberInput from 'vue-phone-number-input';
import 'vue-phone-number-input/dist/vue-phone-number-input.css';
import { sendersUrl } from '@/utils/urls.js';
import { getData, postData, putData } from '@/utils/apiHelper.js';

import themeConfig from '@/theme/themeConfig';
import showAlert from '@/components/sweet-alert-popup.js';

// import icons
import ExitIcon from '@/assets/images/icons/new/exit_arrow_new.svg';

export default {
  name: 'CreateIntegeration',
  middleware: 'auth',
  metaInfo() {
    return {
      title: this.$t('frontend_groovemail_settings_integeration_title'),
    };
  },
  data() {
    return {
      isLoading: false,
      default_country_code: 'GB',
      pageType: 'add',
      submitted: false,
      model: {
        gateway_name: '',
        account_sid: '',
        auth_token: '',
        phone_number: '',
        test_phone_number: '',
        is_default: 0,
      },
      phoneData: {
        countryCallingCode: '44',
        countryCode: 'GB',
        isValid: false,
      },
      testPhoneData: {
        countryCallingCode: '44',
        countryCode: 'GB',
        isValid: false,
      },
    };
  },

  components: {
    VuePhoneNumberInput,
    ExitIcon
  },

  validations: {
    model: {
      gateway_name: {
        required,
        minLength: minLength(3),
        maxLength: maxLength(50),
      },
      account_sid: {
        required,
        minLength: minLength(3),
        maxLength: maxLength(50),
      },
      auth_token: {
        required,
        minLength: minLength(3),
        maxLength: maxLength(50),
      },
    },
  },

  methods: {
    //  Can mail/Upgraded user/Sandbox mode check
    ...mapGetters('mailing', ['getCanMail']),
    //  Can mail/Upgraded user/Sandbox mode check


    // show loader
    showLoader() {
      if(!this.isLoading) {
        this.isLoading = true;
      }
    },

    // hide loader
    hideLoader() {
      if(this.isLoading) {
        this.isLoading = false;
      }
    },

    // error message for the request field
    async requestErrorAlert(error) {
      // hide the loader
      this.hideLoader();
      
      await showAlert({
        iconType: 'alert-error',
        html : `
          <style>.swal2-cancel { color: ${themeConfig.appPrimaryColorInvert} !important; }</style>
          <p class="text-sm text-deep-dark mb-3">${error || this.$t('frontend_backend_error') }</p>
        `,
        showCancelButton: false,
        showCloseButton: true,
        confirmButtonColor: themeConfig.appPrimaryColor,
        confirmButtonText: this.$t('frontend_close'),
      });
    },

    // go back to the integeration settings page on click of back button
    moveBack() {
      // go back to the integeration settings page
      this.$router.push({ name: 'Settings', query: { type: 'integeration' } });
    },

	
    /**
     * Sets senders phone details whenever there is a change in input value.
     * @param {object} event contains updated data.
     */
    getPhoneData(event) {
      if (event.countryCallingCode) {
        this.phoneData.countryCallingCode = event.countryCallingCode;
      }
      if (event.countryCode) {
        this.phoneData.countryCode = event.countryCode;
      }
      this.phoneData.isValid = event.isValid;
    },
	
    /**
     * Sets test phone details whenever there is a change in input value.
     * @param {object} event contains updated data.
     */
    getTestPhoneData(event) {
      if (event.countryCallingCode) {
        this.testPhoneData.countryCallingCode = event.countryCallingCode;
      }
      if (event.countryCode) {
        this.testPhoneData.countryCode = event.countryCode;
      }
      this.testPhoneData.isValid = event.isValid;
    },

    // Toogles default sender value.
    changedefault() {
      this.model.is_default = this.model.is_default !== 1 ? 1 : 0;
    },

    // Validates twilio integration form.
    validateTwilioIntegrationForm() {
      this.submitted = true;
      this.$v.model.gateway_name.$touch();
      this.$v.model.account_sid.$touch();
      this.$v.model.auth_token.$touch();
      if (
        this.$v.model.gateway_name.$invalid ||
        this.$v.model.account_sid.$invalid ||
        this.$v.model.auth_token.$invalid ||
        !this.phoneData.isValid ||
        !this.testPhoneData.isValid
      ) {
        return false;
      }

      return true;
    },

    // Create or Update SMS sender.
    async createNewSmsSender() {
      // check if forms is valid
      if (!this.validateTwilioIntegrationForm()) {
        return;
      }

      // show loader
      this.showLoader();

      const integerationData = {
        gateway_name: this.model.gateway_name,
        sid: this.model.account_sid,
        token: this.model.auth_token,
        country_code: `+${  this.phoneData.countryCallingCode}`,
        from_number: this.model.phone_number,
        is_default: this.model.is_default,
        country_symbol: this.phoneData.countryCode,
        testing_number_code: `+${  this.testPhoneData.countryCallingCode}`,
        testing_number: this.model.test_phone_number,
      };

      if (this.pageType === 'add') {
        try {
          const res = await postData(sendersUrl.smsSenders, integerationData);
          this.$emit('smsSenderCreated', res.data);

          // hide loader
          this.hideLoader();
        } catch (error) {
          // show error alert
          this.displayErrorDialog(error);
        }
      }
    },

    // Create or Update SMS sender.
    async createIntegerations() {
      // check if forms is valid
      if (!this.validateTwilioIntegrationForm()) {
        return;
      }

      // show loader
      this.showLoader();
      
      const integerationData = {
        gateway_name: this.model.gateway_name,
        sid: this.model.account_sid,
        token: this.model.auth_token,
        country_code: `+${  this.phoneData.countryCallingCode}`,
        from_number: this.model.phone_number,
        is_default: this.model.is_default,
        country_symbol: this.phoneData.countryCode,
        testing_number_code: `+${  this.testPhoneData.countryCallingCode}`,
        testing_number: this.model.test_phone_number,
      };
      
      if (this.pageType === 'add') {
        try {
          const res = await postData(sendersUrl.smsSenders, integerationData);

          if (this.$route.name === 'CreateIntegeration') {
            this.$router.push({
              name: 'Settings',
              query: { type: 'integeration' },
            });
          } else {
            this.$emit('smsSenderCreated', res.data);
          }

          // hide loader
          this.hideLoader();
        } catch (error) {
          // show error alert
          this.displayErrorDialog(error);
        }
      } else if (this.pageType === 'edit') {
        try {
          await putData(`${sendersUrl.smsSenders}/${this.$route.params.id}`, integerationData);

          this.$router.push({ 
            name: 'Settings',
            query: { type: 'integeration' },
          });

          // hide loader
          this.hideLoader();
        } catch (error) {
          // show error alert
          this.displayErrorDialog(error);
        }
      }
    },

    /**
     * Used to display error message.
     * @param {object} error contains error details.
    */
    displayErrorDialog(error) {
      if (error.response && error.response.status === 409) {
        this.requestErrorAlert(this.$t('frontend_groovemail_settings_integeration_sid_already_saved'));
      } else if (error.response && error.response.status === 404) {
        this.requestErrorAlert(this.$t('frontend_groovemail_settings_integeration_unsuccessful_account_verification'));
      } else {
        this.requestErrorAlert(error);
      }
    },
  },

  computed: {
    //  Can mail/Upgraded user/Sandbox mode check
	  /**
     * Returns 1 if user can mail
               0 if user can't mail.
     * @returns {string} returns whether user can mail or not.
     */
    userCanMail() {
      return this.getCanMail();
    },
    //  Can mail/Upgraded user/Sandbox mode check
  },

  // Sets data properties value to sms sender details in case of edit sender.
  async created() {
    if (this.$route.params.id && this.$route.name === 'EditIntegeration') {
	    // show loader
      this.showLoader();

      const integerationData = await getData(
        `${sendersUrl.smsSenders}/${this.$route.params.id}`, null, true
      );
      this.pageType = 'edit';
      this.model.gateway_name = integerationData?.data?.gateway_name;
      this.model.account_sid = integerationData?.data?.sid;
      this.model.auth_token = integerationData?.data?.token;
      this.model.phone_number = integerationData?.data?.from_number;
      this.model.is_default = integerationData?.data?.is_default;
      this.default_country_code = integerationData?.data?.country_symbol;

      // hide loader
      this.hideLoader();
    }
  },
};
</script>

<style>
.sms-sender-phone-input {
  color: #8689a4;
}
</style>

<style scoped>
.gm-integration-settings {
  min-height: 100%;
  padding: 28px;
  color: var(--gs-foreground);
  background: var(--gs-background);
}

.gm-integration-settings__card {
  max-width: 960px;
  margin: 0 auto;
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-lg);
  box-shadow: var(--gs-shadow-sm);
}

.gm-integration-settings__body {
  min-width: 0;
}

@media (max-width: 640px) {
  .gm-integration-settings {
    padding: 16px;
  }

  .gm-integration-settings__card {
    border-radius: var(--gs-radius-md);
  }

  .gm-integration-settings__header,
  .gm-integration-settings__body {
    padding-right: 18px;
    padding-left: 18px;
  }
}
</style>
